import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  House,
  Drop,
  Sparkle,
  ShieldCheck,
  PawPrint,
  Wine,
  ForkKnife,
  Laptop,
  UsersThree,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { BrandHeroCopy } from './BrandHero';

const SLIDES = [
  {
    brand: 'PetCart',
    logo: '/images/logo-petcart.png',
    image: '/images/dog-training.jpg',
    eyebrow: 'A pet lifestyle company',
    phrases: ['Happy Pets.', 'Happier Pet Parents.'],
    subtitle: 'Celebrating the bond. Enhancing every day. Curating experiences for every pet parent.',
    tone: 'burgundy',
    wash: 'from-black/45 via-black/20 to-transparent',
    accent: 'hsl(var(--primary))',
    cta: 'Explore PetCart',
    ctaPath: '#featured-on',
  },
  {
    brand: 'PetCart Nest',
    logo: ['/images/logo-petcart.png', '/images/logo-nest.png'],
    image: '/images/facility-dogs-playing.jpg',
    eyebrow: 'Premium pet resort',
    phrases: ['More than a stay,', "It's their happy place."],
    subtitle: 'Where tails wag, hearts relax and every stay feels like home.',
    tone: 'burgundy',
    wash: 'from-black/45 via-black/20 to-transparent',
    accent: 'hsl(var(--primary))',
    cta: 'Visit PetCart Nest',
    ctaPath: '/petcart-nest',
    features: [
      { icon: House, label: 'Premium stay & daycare', sub: 'A campus that feels like home' },
      { icon: Drop, label: 'Swimming & hydro fun', sub: 'Supervised splash sessions' },
      { icon: Sparkle, label: 'Grooming spa', sub: 'Fresh, calm, cared for' },
    ],
  },
  {
    brand: 'Freshtails',
    logo: '/images/logo-freshtails.png',
    image: '/images/view.png',
    eyebrow: 'A PetCart venture',
    phrases: ['Fresh in every way.', 'Happy every day.'],
    subtitle: 'Daily walks with trained walkers. Everything your dog needs to stay fresh, healthy and happy.',
    tone: 'forest',
    wash: 'from-black/45 via-black/20 to-transparent',
    accent: '#1b4d3e',
    cta: 'Visit Freshtails',
    ctaPath: '/freshtails',
    features: [
      { icon: ShieldCheck, label: 'Trained & verified walkers', sub: 'Background-checked care' },
      { icon: PawPrint, label: 'Safe, hygienic care', sub: 'Dog-first handling' },
      { icon: UsersThree, label: 'Trusted by pet parents', sub: 'Live updates after every walk' },
    ],
  },
  {
    brand: 'Wag & Wine',
    logo: '/images/logo-wag-wine.png',
    image: '/images/wag-and-wine.png',
    eyebrow: 'A PetCart venture',
    phrases: ['Everything is fine', 'when you are with', 'Wag & Wine.'],
    subtitle: 'A cozy pet cafe where wagging tails, good vibes and great times come together.',
    tone: 'burgundy',
    wash: 'from-black/45 via-black/20 to-transparent',
    accent: '#d54a30',
    cta: 'Visit Wag & Wine',
    ctaPath: '/wag-and-wine',
    featuresInline: true,
    features: [
      { icon: Wine, label: 'Petcafe', sub: 'Hooman friendly' },
      { icon: ForkKnife, label: 'Food for both', sub: 'Plates for you and them' },
      { icon: PawPrint, label: 'Play areas', sub: 'Doggie pool & lawn' },
      { icon: Laptop, label: 'Work from cafe', sub: 'Stay as long as you like' },
    ],
  },
];

const INTERVAL = 7000;

const Hero = () => {
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive((current) => (current + 1) % SLIDES.length);
    }, INTERVAL);
  };

  useEffect(() => {
    startTimer();
    const onVisibility = () => {
      if (document.hidden) clearInterval(timerRef.current);
      else startTimer();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const selectSlide = (index) => {
    setActive(index);
    startTimer();
  };

  const goToSlide = (slide) => {
    if (!slide.ctaPath) return;
    if (slide.ctaPath.startsWith('#')) {
      document.querySelector(slide.ctaPath)?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    navigate(slide.ctaPath);
  };

  const slide = SLIDES[active];

  return (
    <section
      className="landing-hero-section relative flex min-h-[100svh] flex-col overflow-hidden bg-neutral-950 sm:min-h-[680px] lg:min-h-[92vh]"
      onMouseEnter={() => clearInterval(timerRef.current)}
      onMouseLeave={startTimer}
      aria-roledescription="carousel"
      aria-label="PetCart brands"
    >
      <style>{`
        @keyframes heroProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>

      {SLIDES.map((item, index) => (
        <div
          key={item.brand}
          className={`absolute inset-0 transition-opacity duration-700 ${index === active ? 'opacity-100' : 'opacity-0'}`}
          aria-hidden={index !== active}
        >
          <img
            src={item.image}
            alt=""
            className="h-full w-full object-cover object-left lg:object-center"
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className={`absolute inset-0 bg-gradient-to-r ${item.wash}`} />
        </div>
      ))}

      <div className="landing-hero-offset relative z-10 mx-auto flex w-full max-w-6xl flex-1 flex-col items-center px-6 pb-10 sm:px-8 sm:pb-12 lg:items-start lg:px-10 lg:pb-12">
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center lg:items-start">
          <BrandHeroCopy
            logo={slide.logo}
            logoAlt={`${slide.brand} logo`}
            eyebrow={slide.eyebrow}
            phrases={slide.phrases}
            subtitle={slide.subtitle}
            features={slide.features}
            featuresInline={slide.featuresInline}
            tone={slide.tone}
            actions={
              <Button
                onClick={() => goToSlide(slide)}
                className="h-10 rounded-full px-5 text-sm font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: slide.accent }}
              >
                {slide.cta}
                <ArrowRight size={14} weight="bold" />
              </Button>
            }
          />
        </div>

        <div className="mt-10 w-full grid grid-cols-4 gap-2 sm:mt-12 sm:gap-3">
          {SLIDES.map((item, index) => {
            const isActive = index === active;
            return (
              <button
                key={item.brand}
                type="button"
                onClick={() => selectSlide(index)}
                className="group flex w-full flex-col items-center text-center lg:items-start lg:text-left"
                aria-label={`Show ${item.brand}`}
                aria-current={isActive ? 'true' : undefined}
              >
                <div className="mb-2 h-[3px] w-full overflow-hidden rounded-full bg-white/25">
                  {isActive && (
                    <div
                      className="h-full rounded-full"
                      style={{
                        backgroundColor: item.accent,
                        animation: `heroProgress ${INTERVAL}ms linear forwards`,
                      }}
                    />
                  )}
                </div>
                <p className={`text-xs font-semibold sm:text-sm ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'}`}>
                  {item.brand}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;
